import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { FC } from "react"
import { useTranslation } from "react-i18next"
import { Sstv2MigrationEvidenceRun } from "shared/sstv2MigrationEvidenceRun"

/**
 * The evidence artifact for a finished (or in-flight) run: the headline
 * completeness verdict (observed == sent) plus a per-step drill-down with the
 * attributed CloudWatch consumer log rows. The verdict is the single most
 * important readout — it is the proof the run "did its job".
 */
export const Sstv2EvidenceRunResult: FC<{ run: Sstv2MigrationEvidenceRun }> = ({
  run,
}) => {
  const { t } = useTranslation()
  const verdict = run.verdict

  return (
    <Box data-ui="UI-SL2W-267">
      {verdict && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
            bgcolor: verdict.ok ? "success.light" : "action.hover",
          }}
        >
          <Typography
            sx={{
              fontSize: "1.25rem",
              fontWeight: 800,
              fontFamily: "monospace",
              color: verdict.ok ? "success.dark" : "text.primary",
              whiteSpace: "nowrap",
            }}
          >
            {verdict.ok ? "✅" : "⚠"} {verdict.eventsObserved} {t("evidenceIdentified")}
            {" · "}
            {verdict.eventsPublished} {t("evidenceDelivered")}
          </Typography>
          <Box sx={{ minWidth: 240, flex: 1 }}>
            <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
              {verdict.ok ? t("evidenceVerdictOk") : t("evidenceVerdictPartial")}
            </Typography>
            {verdict.missingTypes?.length > 0 && (
              <Typography sx={{ fontSize: "0.76rem", color: "text.secondary", mt: 0.5 }}>
                {t("evidenceMissingTypes")}: {verdict.missingTypes.join(", ")}
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {run.steps.map((step) => (
        <Accordion key={step.key} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                {step.title}
              </Typography>
              {step.responseSummary && (
                <Chip size="small" variant="outlined" label={step.responseSummary} />
              )}
              {!step.isStatus && (
                <Chip
                  size="small"
                  color={step.observedCount > 0 ? "primary" : "default"}
                  label={`${step.observedCount} ${t("observed")}`}
                />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            {step.events?.length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
                {step.events.map((e) => (
                  <Chip key={e} size="small" color="primary" variant="outlined" label={e} />
                ))}
              </Stack>
            )}
            {step.cwLogRows && step.cwLogRows.length > 0 ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("time")}</TableCell>
                    <TableCell>{t("level")}</TableCell>
                    <TableCell>{t("event")}</TableCell>
                    <TableCell>messageId</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {step.cwLogRows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.72rem" }}>
                        {r.iso}
                      </TableCell>
                      <TableCell sx={{ fontSize: "0.72rem" }}>{r.level ?? ""}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.72rem", color: "primary.main" }}>
                        {r.event ?? ""}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "monospace", fontSize: "0.72rem" }}>
                        {r.messageId ?? ""}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography sx={{ fontSize: "0.78rem", color: "text.secondary" }}>
                {step.isStatus ? t("noSqsEvents") : t("evidenceNoLogRows")}
              </Typography>
            )}
            <Typography sx={{ fontSize: "0.76rem", color: "text.secondary", mt: 1 }}>
              {t("sent")} (NumberOfMessagesSent): {step.sentMetric ?? "—"}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  )
}

export default Sstv2EvidenceRunResult
